import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        redirect: '/objects',
      },
      {
        path: 'objects',
        name: 'objects',
        component: () => import('@/pages/ObjectsPage.vue'),
      },
      {
        path: 'objects/:id',
        name: 'object-detail',
        component: () => import('@/pages/ObjectDetailPage.vue'),
        props: true,
      },
      {
        path: 'objects/new',
        name: 'object-new',
        component: () => import('@/pages/ObjectFormPage.vue'),
      },
      {
        path: 'objects/:id/edit',
        name: 'object-edit',
        component: () => import('@/pages/ObjectFormPage.vue'),
        props: true,
      },
      {
        path: 'checks',
        redirect: '/subjects',
      },
      {
        path: 'checks/:id',
        redirect: to => `/subjects/${to.params.id}`,
      },
      {
        path: 'subjects',
        name: 'subjects',
        component: () => import('@/pages/SubjectsPage.vue'),
      },
      {
        path: 'profile-candidates',
        name: 'profile-candidates',
        component: () => import('@/pages/ProfileCandidatesPage.vue'),
      },
      {
        path: 'subjects/:subjectId/social-links/:socialLinkId',
        name: 'subject-social-link-detail',
        component: () => import('@/pages/SubjectSocialLinkDetailPage.vue'),
        props: true,
      },
      {
        path: 'subjects/:id',
        name: 'subject-detail',
        component: () => import('@/pages/SubjectDetailPage.vue'),
        props: true,
      },
      {
        path: 'admin',
        name: 'admin',
        component: () => import('@/pages/AdminPage.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/pages/AdminUsersPage.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/categories',
        name: 'admin-categories',
        component: () => import('@/pages/AdminCategoriesPage.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/link-types',
        name: 'admin-link-types',
        component: () => import('@/pages/AdminLinkTypesPage.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/profile-matcher',
        name: 'admin-profile-matcher',
        component: () => import('@/pages/AdminProfileMatcherPage.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },
  {
    path: '/callback',
    name: 'callback',
    component: () => import('@/pages/CallbackPage.vue'),
  },
  {
    path: '/access-denied',
    name: 'access-denied',
    component: () => import('@/pages/AccessDeniedPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore()

  // Allow callback page
  if (to.name === 'callback') {
    next()
    return
  }

  // Allow access-denied page for authenticated users
  if (to.name === 'access-denied') {
    if (auth.isAuthenticated) {
      next()
    } else {
      auth.login()
    }
    return
  }

  // Not authenticated - redirect to login
  if (!auth.isAuthenticated) {
    auth.login()
    return
  }

  // Authenticated but no access - redirect to access denied
  if (!auth.hasAccess) {
    next({ name: 'access-denied' })
    return
  }

  // Requires admin but user is not admin
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    next({ name: 'home' })
    return
  }

  next()
})

export default router
